import { motion } from "framer-motion";
import { Check, Zap, Award, Shield, HeadphonesIcon, Star, ArrowRight, MessageCircle, CreditCard, Server, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const plans = [
  {
    name: "Silver IP Voice",
    techPrefix: "10003",
    tagline: "Price-Oriented",
    color: "bg-slate-700",
    textColor: "text-slate-700",
    borderColor: "border-slate-200",
    description: "Best for price-sensitive traffic. Our Silver routes deliver stable, continuously monitored connections at the most competitive rates available in the market.",
    features: [
      "Tech Prefix: 10003",
      "Competitive per-minute rates",
      "Continuous route monitoring",
      "Stable connections",
      "Standard support",
      "235+ country coverage"
    ],
    cta: "Get Silver Rates",
    highlight: false
  },
  {
    name: "Gold IP Voice",
    techPrefix: "10001",
    tagline: "Quality-Focused",
    color: "bg-brand-500",
    textColor: "text-brand-500",
    borderColor: "border-brand-400",
    description: "Quality-focused retail route ideal for consumer services and business calls. We constantly monitor traffic to ensure high ASR, high ACD, and low PDD on every destination.",
    features: [
      "Tech Prefix: 10001",
      "Full CLI delivery",
      "High ASR & ACD",
      "Low PDD",
      "Priority 24/7 support",
      "Real-time monitoring"
    ],
    cta: "Get Gold Rates",
    highlight: true,
    badge: "Most Popular"
  },
  {
    name: "Platinum IP Voice",
    techPrefix: "10002",
    tagline: "Highest Quality",
    color: "bg-indigo-600",
    textColor: "text-indigo-600",
    borderColor: "border-indigo-300",
    description: "The absolute highest quality tier. We guarantee full CLI delivery and retail-grade voice through direct Tier 1 carrier partnerships and dedicated routes around the world.",
    features: [
      "Tech Prefix: 10002",
      "Guaranteed full CLI",
      "Tier 1 & direct routes",
      "Premium voice quality",
      "24/7 VIP support",
      "Dedicated route monitoring"
    ],
    cta: "Get Platinum Rates",
    highlight: false
  }
];

const callCenterPlan = {
  name: "Call Center IP Voice",
  tagline: "Engineered for High-CPM Traffic",
  description: "Built for call center operators who run low average call duration (ACD) and high calls-per-minute (CPM) traffic. We've selected carriers with proven tolerance for this traffic type — so your routes stay up.",
  features: [
    "Optimised for low ACD",
    "High CPM tolerance",
    "Selected carrier network",
    "Dedicated monitoring",
    "Competitive rates",
    "Stable long-term routing"
  ]
};

const sipDetails = {
  host: "sip.voiphub.in",
  wss: "wss://sip.voiphub.in/ws",
  port: "5060 (UDP/TCP) / 5061 (TLS)",
  codecs: "G.711 alaw/ulaw, G.729"
};

const PricingPage = () => {
  return (
    <div data-testid="pricing-page">

      {/* Hero */}
      <section className="bg-slate-900 text-white py-24 noise-bg" data-testid="pricing-hero">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-brand-500/20 text-brand-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Zap className="w-4 h-4" />
              Transparent, Prepaid Pricing
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-5">Choose Your Plan</h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-6">
              Silver, Gold, or Platinum — all on the same SIP host. Switch quality tiers just by changing your tech prefix.
              Free trial credits included on your first top-up.
            </p>
            <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
              <Server className="w-5 h-5 text-brand-400" />
              <span className="text-slate-300 text-sm font-medium">SIP Host: <strong className="text-white font-mono">sip.voiphub.in</strong></span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Free Trial Banner */}
      <section className="bg-brand-50 border-b border-brand-100 py-5">
        <div className="container-custom text-center">
          <p className="text-brand-700 font-semibold text-base">
            🎁 <strong>Free Trial Credits</strong> — New accounts receive complimentary credits to test routes and quality before committing. No credit card needed to register.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-light -mt-4 relative z-10" data-testid="pricing-cards">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative flex flex-col rounded-2xl bg-white border-2 transition-all overflow-hidden ${
                  plan.highlight
                    ? `${plan.borderColor} shadow-2xl scale-105 z-10`
                    : 'border-slate-200 shadow-lg hover:shadow-xl hover:-translate-y-1'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {plan.badge && (
                  <div className="absolute top-0 left-0 right-0 bg-brand-500 text-white text-xs font-bold py-2 text-center tracking-wider uppercase">
                    {plan.badge}
                  </div>
                )}

                {/* Header */}
                <div className={`${plan.color} p-6 ${plan.badge ? 'mt-8' : ''}`}>
                  <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                  <p className="text-white/80 text-sm mt-1">{plan.tagline}</p>
                  <div className="mt-4 bg-white/15 rounded-xl p-3 inline-block">
                    <p className="text-white/70 text-xs uppercase tracking-wider">Tech Prefix</p>
                    <p className="text-3xl font-mono font-bold text-white mt-0.5">{plan.techPrefix}</p>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 flex flex-col flex-grow">
                  <p className="text-slate-600 mb-6 leading-relaxed flex-grow">{plan.description}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-brand-50 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-brand-500" />
                        </div>
                        <span className={`text-sm font-medium ${j === 0 ? `${plan.textColor} font-bold` : 'text-slate-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/rates" className="mt-auto">
                    <Button
                      className={`w-full rounded-full py-5 font-semibold ${
                        plan.highlight
                          ? 'bg-brand-500 hover:bg-brand-600 text-white shadow-lg shadow-brand-500/25'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                      }`}
                    >
                      {plan.cta} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* SIP Connection Details */}
          <motion.div
            className="mt-14 bg-slate-900 rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Server className="w-6 h-6 text-brand-400" />
              <h3 className="text-xl font-bold">SIP Connection Details</h3>
              <span className="text-slate-400 text-sm">— same host for all plans, just change the prefix</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { label: "SIP Host / Domain", value: sipDetails.host },
                { label: "WebSocket (WebRTC)", value: sipDetails.wss },
                { label: "SIP Port", value: sipDetails.port },
                { label: "Supported Codecs", value: sipDetails.codecs }
              ].map((d, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{d.label}</p>
                  <p className="text-white font-mono text-sm font-semibold">{d.value}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-sm mt-5">
              Use your SIP credentials from the portal. Set the tech prefix as a destination prefix or dial-string prefix in your SIP client.
              Our browser-based dialer handles this automatically.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Call Center Plan */}
      <section className="section-alt" data-testid="call-center-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <HeadphonesIcon className="w-4 h-4" />
                Specialized Solution
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{callCenterPlan.name}</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">{callCenterPlan.description}</p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {callCenterPlan.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-brand-500 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4">
                <Link to="/rates">
                  <Button className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-6">View Rates</Button>
                </Link>
                <a href="https://t.me/voiphub_support" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="rounded-full px-6 border-slate-300">
                    <MessageCircle className="mr-2 w-4 h-4" /> Contact Sales
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-slate-900 rounded-2xl p-8 shadow-xl">
              <Globe className="w-10 h-10 text-brand-400 mb-4" />
              <h4 className="text-white text-xl font-bold mb-3">A-Z Termination Network</h4>
              <p className="text-slate-400 mb-6 leading-relaxed">
                All VoipHub plans include A-Z coverage. We route traffic through Tier 1 carriers and direct partnerships
                to 235+ countries — ensuring CLI, low PDD, and high ASR regardless of destination.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "India (all operators)", "USA & Canada", "UK & Europe", "Middle East",
                  "Southeast Asia", "Africa", "Latin America", "Pacific Islands"
                ].map((region, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                    {region}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* All Plans Include */}
      <section className="section-light" data-testid="features-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">All Plans Include</h2>
            <p className="text-slate-600">Core features available across all pricing tiers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Secure SIP/TLS", desc: "Encrypted voice transport" },
              { icon: Zap, title: "Real-time Rates", desc: "Instant rate table updates" },
              { icon: HeadphonesIcon, title: "24/7 Support", desc: "Via Telegram @voiphub_support" },
              { icon: Star, title: "No Setup Fees", desc: "Start immediately" },
              { icon: CreditCard, title: "Free Trial Credits", desc: "On first account top-up" },
              { icon: Globe, title: "235+ Countries", desc: "Full A-Z coverage" },
              { icon: Server, title: "Browser Dialer", desc: "WebRTC softphone built-in" },
              { icon: Award, title: "Prepaid Billing", desc: "No surprise invoices" }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-brand-500" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                <p className="text-slate-500 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark" data-testid="pricing-cta">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Start Terminating Calls Today</h2>
            <p className="text-slate-300 mb-3 max-w-xl mx-auto">
              Create your account, deposit credits, and you're live in minutes. Free trial credits on first top-up.
            </p>
            <p className="text-brand-400 font-semibold text-sm mb-8">
              Minimum deposit: $100 USD · Prepaid · No contracts
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/portal/">
                <Button className="bg-brand-500 hover:bg-brand-600 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-brand-500/30">
                  Create Account <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/referral">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-slate-600 text-white hover:bg-white/10">
                  <Award className="mr-2 w-5 h-5" /> Referral Program
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
